import React, {
  FC,
  ImgHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import checkInViewIntersectionObserver from "utils/isInViewPortIntersectionObserver";
import PlaceIcon from "./PlaceIcon";
import { ImageProps } from "types/Image";

export interface NcImageProps extends ImgHTMLAttributes<HTMLImageElement>, ImageProps{
  containerClassName?: string;
}

const NcImage: FC<NcImageProps> = ({
  containerClassName = "",
  alt = "DZ Insights image",
  src = "",
  className = "object-cover w-full h-full",
  loading = "lazy", 
  decoding = "async",
  fetchPriority,
  skipLazy = false,
  ...args
}) => {
  const isMountedRef = useRef(false);
  const _containerRef = useRef<HTMLDivElement>(null);
  const [__src, set__src] = useState(skipLazy ? src : "");
  const [imageLoaded, setImageLoaded] = useState(skipLazy);

  useEffect(() => {
    isMountedRef.current = true;
    if (skipLazy) {
      set__src(src);
      setImageLoaded(true);
      return () => {
        isMountedRef.current = false;
      };
    }

    if (!_containerRef.current) return;

    checkInViewIntersectionObserver({
      target: _containerRef.current as any,
      distanceFromEnd: 0,
      callback: () => {
        if (!src) {
          if (isMountedRef.current) {
            setImageLoaded(true);
            set__src("");
          }
          return true;
        }
        const imgEl = new Image();
        imgEl.src = src;
        imgEl.onload = () => {
          if (isMountedRef.current) {
            setImageLoaded(true);
            set__src(src);
          }
        };
        return true;
      },
    });

    return () => {
      isMountedRef.current = false;
    };
  }, [src, skipLazy]);

  const renderLoadingPlaceholder = () => {
    return (
      <div
        className={`${className} flex items-center justify-center bg-neutral-200 dark:bg-neutral-6000 text-neutral-100 dark:text-neutral-500`}
      >
      </div>
    );
  };

  return (
    <div
      className={`nc-NcImage ${containerClassName}`}
      data-nc-id="NcImage"
      ref={_containerRef}
    >
      {__src && imageLoaded ? (
        <img
          src={__src}
          className={className}
          alt={alt}
          {...(fetchPriority ? { fetchPriority, fetchpriority: fetchPriority } : {})}
          {...args}
          loading={skipLazy ? "eager" : loading}
          decoding={decoding}
        />
      ) : (
        renderLoadingPlaceholder()
      )}
    </div>
  );
};

export default NcImage;
